import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api.service';

type CreditHistory = 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR';
type IncomeStability = 'STABLE' | 'MODERATE' | 'UNSTABLE';

const LEVEL_BADGE: Record<string, string> = {
  LOW: 'bg-green-100 text-green-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  HIGH: 'bg-red-100 text-red-800',
};

export function RiskManagement() {
  const [customers, setCustomers] = useState<any[]>([]);
  const [dashboard, setDashboard] = useState<{ stats: { total: number; lowRisk: number; mediumRisk: number; highRisk: number }; recentAssessments: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);
  const [form, setForm] = useState({
    customerId: '',
    creditHistory: 'GOOD' as CreditHistory,
    incomeStability: 'STABLE' as IncomeStability,
    debtToIncome: '0.3',
    employmentYears: '',
    creditScore: '',
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [cs, dash] = await Promise.all([apiService.getCustomers(), apiService.getRiskDashboard()]);
      setCustomers(cs);
      setDashboard(dash);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setLastResult(null);
    try {
      const res = await apiService.calculateRiskScore({
        customerId: form.customerId,
        creditHistory: form.creditHistory,
        incomeStability: form.incomeStability,
        debtToIncome: parseFloat(form.debtToIncome),
        employmentYears: form.employmentYears ? parseInt(form.employmentYears, 10) : undefined,
        creditScore: form.creditScore ? parseInt(form.creditScore, 10) : undefined,
      });
      setLastResult(res);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const customerName = (id: string) => {
    const c = customers.find((x) => x.id === id);
    return c ? `${c.firstName} ${c.lastName}` : id.slice(0, 8) + '…';
  };

  const recent = useMemo(() => dashboard?.recentAssessments ?? [], [dashboard]);

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Gestión de riesgo</h1>
        <button onClick={load} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Cargando…' : 'Actualizar'}
        </button>
      </div>

      {error && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Total assessments</p>
            <p className="text-2xl font-bold text-gray-900">{dashboard.stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Riesgo bajo</p>
            <p className="text-2xl font-bold text-green-700">{dashboard.stats.lowRisk}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Riesgo medio</p>
            <p className="text-2xl font-bold text-yellow-700">{dashboard.stats.mediumRisk}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Riesgo alto</p>
            <p className="text-2xl font-bold text-red-700">{dashboard.stats.highRisk}</p>
          </div>
        </div>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Calcular score</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
            <select required value={form.customerId} onChange={(e) => setForm({ ...form, customerId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">Seleccionar…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.firstName} {c.lastName} — {c.email}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Historial crediticio</label>
              <select value={form.creditHistory} onChange={(e) => setForm({ ...form, creditHistory: e.target.value as CreditHistory })} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="EXCELLENT">EXCELLENT</option>
                <option value="GOOD">GOOD</option>
                <option value="FAIR">FAIR</option>
                <option value="POOR">POOR</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estabilidad de ingresos</label>
              <select value={form.incomeStability} onChange={(e) => setForm({ ...form, incomeStability: e.target.value as IncomeStability })} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="STABLE">STABLE</option>
                <option value="MODERATE">MODERATE</option>
                <option value="UNSTABLE">UNSTABLE</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Debt-to-Income</label>
              <input type="number" step="0.01" min={0} max={1.5} required value={form.debtToIncome} onChange={(e) => setForm({ ...form, debtToIncome: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Antigüedad laboral (años)</label>
              <input type="number" min={0} value={form.employmentYears} onChange={(e) => setForm({ ...form, employmentYears: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Credit score externo (opcional)</label>
              <input type="number" min={300} max={850} value={form.creditScore} onChange={(e) => setForm({ ...form, creditScore: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
          </div>
          <button type="submit" disabled={submitting || !form.customerId} className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            {submitting ? 'Calculando…' : 'Calcular score'}
          </button>
        </form>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Resultado</h2>
          {!lastResult ? (
            <p className="text-gray-500 text-sm">Completá el formulario para ver score, nivel y factores.</p>
          ) : (
            <div>
              <div className="flex items-end gap-6 mb-4">
                <div>
                  <p className="text-xs uppercase text-gray-500">Score</p>
                  <p className="text-4xl font-bold text-gray-900">{lastResult.score}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Nivel</p>
                  <span className={'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ' + LEVEL_BADGE[lastResult.level]}>
                    {lastResult.level}
                  </span>
                </div>
              </div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Factores</h3>
              <ul className="text-sm space-y-1">
                {lastResult.factors.map((f: any, i: number) => (
                  <li key={i} className="flex justify-between border-b border-gray-100 py-1">
                    <span>{f.name} <span className="text-gray-500">({JSON.stringify(f.value)})</span></span>
                    <span className={f.delta < 0 ? 'text-red-700' : 'text-gray-700'}>{f.delta}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">Últimos assessments</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {recent.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay assessments aún.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nivel</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recent.map((a) => (
                  <tr key={a.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a.createdAt?.slice(0, 10)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {a.customer ? `${a.customer.firstName} ${a.customer.lastName}` : customerName(a.customerId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{a.score}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ' + (LEVEL_BADGE[a.level] ?? 'bg-gray-100 text-gray-800')}>
                        {a.level}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
